import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { ImageCropper } from '../components/ImageCropper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wand2,
  Copy,
  Check,
  Download,
  RefreshCw,
  Palette,
  Layout as LayoutIcon,
  Eye,
  Phone,
  Upload,
  Mail,
  HelpCircle,
  Chrome,
  Apple,
  Monitor,
  Type,
  Plus,
  Trash2,
  Image,
  Link,
  GripVertical,
  Code,
  Users,
  ChevronLeft,
  ChevronRight,
  Store,
  CheckCircle,
  PenLine,
  QrCode,
  Puzzle,
  MapPin,
  ShoppingBag,
  BarChart3
} from 'lucide-react';
import { Settings, Camera, Save, User, Image as ImageIcon } from 'lucide-react';
import { ApprovalCenter } from './ApprovalCenter';
import { AdMarketplace } from './AdMarketplace';
import AnalyticsDashboard from './AnalyticsDashboard';
import { ActivateModal } from '../components/PremiumGate';
import InstallSignatureWizard from '../components/InstallSignatureWizard';

const TEMPLATE_STYLES = [
  { id: 'executive', name: 'Executive', description: 'Serif · Authoritative', preview: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', font: 'Georgia, serif' },
  { id: 'modern', name: 'Modern', description: 'Sans-serif · Clean', preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', font: 'Segoe UI, sans-serif' },
  { id: 'creative', name: 'Headshot', description: 'Personal Brand', preview: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', font: 'Helvetica Neue, sans-serif' },
  { id: 'signature_pro', name: 'Signature Pro', description: 'Photo Right · Framed', preview: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #3b82f6 100%)', font: 'Segoe UI, sans-serif', badge: 'NEW' },
  { id: 'studio', name: 'Studio', description: 'Dark Card · Photo Right', preview: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', font: 'Segoe UI, sans-serif', badge: 'NEW' },
  { id: 'gradient_edge', name: 'Gradient Edge', description: 'L-Frame · Photo Right', preview: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)', font: 'Segoe UI, sans-serif', badge: 'NEW' },
  { id: 'classic', name: 'Classic', description: 'Clean · Professional', preview: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)', font: 'Helvetica Neue, sans-serif' },
  { id: 'minimalist', name: 'Minimalist', description: 'Ultra-light · Refined', preview: 'linear-gradient(135deg, #bdc3c7 0%, #2c3e50 100%)', font: 'Helvetica Neue, sans-serif' },
  { id: 'bold', name: 'Bold', description: 'Strong · Impactful', preview: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)', font: 'Impact, sans-serif' },
  { id: 'elegant', name: 'Elegant', description: 'Luxury · Sophisticated', preview: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)', font: 'Didot, Georgia, serif' },
  { id: 'techno', name: 'Techno', description: 'Monospace · Digital', preview: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)', font: 'Consolas, monospace' },
  { id: 'handwritten', name: 'Handwritten', description: 'Personal · Warm', preview: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)', font: 'Brush Script MT, cursive' },
  { id: 'corporate', name: 'Legal', description: 'Corporate · Formal', preview: 'linear-gradient(135deg, #141e30 0%, #243b55 100%)', font: 'Georgia, serif' },
  { id: 'corporate_classic', name: 'Corporate Classic', description: 'Enterprise · Professional', preview: 'linear-gradient(135deg, #003366 0%, #004080 50%, #0059b3 100%)', font: 'Arial, sans-serif' },
  { id: 'premium_corporate', name: 'Corporate Tile Reveal', description: 'Left-to-Right Slice Reveal', preview: 'linear-gradient(135deg, #0f172a 0%, #334155 50%, #0ea5e9 100%)', font: 'Arial, sans-serif', badge: 'PREMIUM', premium: true },
  { id: 'premium_visual', name: 'Modern Tile Reveal', description: 'Diagonal Grid Reveal', preview: 'linear-gradient(135deg, #1e293b 0%, #7c3aed 50%, #ec4899 100%)', font: 'Segoe UI, sans-serif', badge: 'PREMIUM', premium: true },
  { id: 'premium_marketing', name: 'Luxury Tile Reveal', description: 'Top-Down Fade Reveal', preview: 'linear-gradient(135deg, #0f172a 0%, #059669 50%, #10b981 100%)', font: 'Helvetica Neue, sans-serif', badge: 'PREMIUM', premium: true }
];

const COLOR_SCHEMES = [
  { id: 'navy', name: 'Navy', color: '#1e3a5f', accent: '#3b82f6' },
  { id: 'emerald', name: 'Emerald', color: '#065f46', accent: '#10b981' },
  { id: 'burgundy', name: 'Burgundy', color: '#7f1d1d', accent: '#ef4444' },
  { id: 'royal', name: 'Royal', color: '#4c1d95', accent: '#8b5cf6' },
  { id: 'charcoal', name: 'Charcoal', color: '#1f2937', accent: '#6b7280' },
  { id: 'ocean', name: 'Ocean', color: '#0e7490', accent: '#06b6d4' },
  { id: 'sunset', name: 'Sunset', color: '#c2410c', accent: '#f97316' },
  { id: 'forest', name: 'Forest', color: '#166534', accent: '#22c55e' }
];

// Google Fonts options
const FONT_FAMILIES = [
  { id: 'default', name: 'Default (Template)', value: '' },
  { id: 'roboto', name: 'Roboto', value: "'Roboto', sans-serif" },
  { id: 'open-sans', name: 'Open Sans', value: "'Open Sans', sans-serif" },
  { id: 'lato', name: 'Lato', value: "'Lato', sans-serif" },
  { id: 'montserrat', name: 'Montserrat', value: "'Montserrat', sans-serif" },
  { id: 'playfair', name: 'Playfair Display', value: "'Playfair Display', serif" },
  { id: 'merriweather', name: 'Merriweather', value: "'Merriweather', serif" },
  { id: 'poppins', name: 'Poppins', value: "'Poppins', sans-serif" },
  { id: 'raleway', name: 'Raleway', value: "'Raleway', sans-serif" },
  { id: 'oswald', name: 'Oswald', value: "'Oswald', sans-serif" },
  { id: 'source-sans', name: 'Source Sans Pro', value: "'Source Sans Pro', sans-serif" }
];

// Text color options for signature
const TEXT_COLORS = [
  { id: 'default', name: 'Default', value: '' },
  { id: 'dark', name: 'Dark Gray', value: '#1f2937' },
  { id: 'black', name: 'Black', value: '#000000' },
  { id: 'navy', name: 'Navy Blue', value: '#1e3a5f' },
  { id: 'slate', name: 'Slate', value: '#475569' },
  { id: 'charcoal', name: 'Charcoal', value: '#374151' },
  { id: 'brown', name: 'Brown', value: '#78350f' },
  { id: 'maroon', name: 'Maroon', value: '#7f1d1d' },
  { id: 'forest', name: 'Forest Green', value: '#14532d' },
  { id: 'purple', name: 'Deep Purple', value: '#581c87' }
];

// Ad button color options for professionals to customize
const AD_BUTTON_COLORS = [
  { id: 'green', name: 'Green', value: '#22c55e' },
  { id: 'blue', name: 'Blue', value: '#3b82f6' },
  { id: 'purple', name: 'Purple', value: '#8b5cf6' },
  { id: 'orange', name: 'Orange', value: '#f97316' },
  { id: 'red', name: 'Red', value: '#ef4444' },
  { id: 'teal', name: 'Teal', value: '#14b8a6' },
  { id: 'pink', name: 'Pink', value: '#ec4899' },
  { id: 'amber', name: 'Amber', value: '#f59e0b' }
];

// Sample ads for preview with photos
const SAMPLE_ADS = [
  {
    name: 'Sarah Martinez',
    profession: 'Loan Officer',
    company: 'First National Lending',
    phone: '(555) 234-5678',
    initial: 'S',
    photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face'
  },
  {
    name: 'Michael Thompson',
    profession: 'Insurance Agent',
    company: 'SecureLife Insurance',
    phone: '(555) 345-6789',
    initial: 'M',
    photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
  }
];

// Email Preview Component - Shows signature in email context
const EmailPreview = ({ signatureHtml, userName }) => (
  <div>
    {/* Signature */}
    <div>
      {signatureHtml ? (
        <div className="signature-preview" dangerouslySetInnerHTML={{ __html: signatureHtml }} />
      ) : (
        <p className="text-gray-400 italic text-sm">[Your signature will appear here]</p>
      )}
    </div>
  </div>
);

// Sample Ad Card Component - with professional photo, green call button, larger size
const SampleAdCard = ({ ad, colorScheme, buttonColor = '#22c55e' }) => {
  return (
    <div className="flex items-center gap-4 py-4 px-5 rounded-xl border border-gray-100 bg-white hover:shadow-lg transition-shadow">
      <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600">
        {ad.photo ? (
          <img src={ad.photo} alt={ad.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-white text-xl font-bold">{ad.initial}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-base text-gray-900 leading-tight">{ad.name}</p>
        <p className="text-sm text-gray-500 mt-0.5">{ad.profession} {ad.company ? `· ${ad.company}` : ''}</p>
      </div>
      <a
        href={`tel:${ad.phone.replace(/\D/g, '')}`}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors shadow-md hover:opacity-90"
        style={{ backgroundColor: buttonColor }}
        data-testid="ad-call-btn"
      >
        <Phone className="w-4 h-4" />
        Call Now
      </a>
    </div>
  );
};

// Ad Slideshow Component - auto-rotates + manual arrows/dots - LARGER with profile photos
const AdSlideshow = ({ ads, colorScheme, buttonColor = '#22c55e' }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const scheme = COLOR_SCHEMES.find(c => c.id === colorScheme) || COLOR_SCHEMES[0];

  useEffect(() => {
    if (paused || !ads || ads.length <= 1) return;
    const timer = setInterval(() => setCurrent(p => (p + 1) % ads.length), 3500);
    return () => clearInterval(timer);
  }, [paused, ads]);

  if (!ads || ads.length === 0) return null;

  const ad = ads[current];
  const prev = () => setCurrent(p => (p - 1 + ads.length) % ads.length);
  const next = () => setCurrent(p => (p + 1) % ads.length);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="ad-slideshow"
    >
      <div className="flex items-center gap-4 py-4 px-5 rounded-xl border border-gray-200 bg-white transition-all duration-300 shadow-sm">
        <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{background: `linear-gradient(135deg, ${scheme.color} 0%, ${scheme.accent} 100%)`}}>
          {ad.photo ? (
            <img src={ad.photo} alt={ad.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-white text-lg font-bold">{ad.initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-base text-gray-900 leading-tight">{ad.name}</p>
          <p className="text-sm text-gray-500 mt-0.5">{ad.profession} {ad.company ? `· ${ad.company}` : ''}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <a
            href={`tel:${ad.phone?.replace(/\D/g, '') || ''}`}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-90 shadow-md"
            style={{backgroundColor: buttonColor}}
            data-testid="ad-call-btn"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
          {ad.qr_url && (
            <img src={ad.qr_url} alt="QR" className="w-12 h-12 rounded-lg border border-gray-100" />
          )}
        </div>
      </div>
      {/* Navigation arrows */}
      {ads.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
            data-testid="slideshow-prev"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
            data-testid="slideshow-next"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </>
      )}
      {/* Dots */}
      {ads.length > 1 && (
        <div className="flex justify-center gap-1 mt-2">
          {ads.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-1.5 h-1.5 rounded-full transition-all ${i === current ? 'w-4' : 'opacity-40'}`}
              style={{backgroundColor: i === current ? scheme.accent : '#9ca3af'}}
              data-testid={`slideshow-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Integration Instructions Component
const IntegrationInstructions = () => (
  <Dialog>
    <DialogTrigger asChild>
      <Button variant="outline" size="sm" className="gap-2">
        <HelpCircle className="w-4 h-4" />
        How to Install
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          How to Add Your Signature to Email
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-6 py-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">
            Click <strong>"Copy Signature"</strong> to copy the visual signature. Then paste it directly into your email client's signature settings. It will paste as a formatted signature, not code.
          </p>
        </div>

        {/* Gmail */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Chrome className="w-5 h-5 text-red-500" />
            <h3 className="font-semibold">Gmail</h3>
          </div>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside ml-2">
            <li>Click <strong>"Copy Signature"</strong> on ConnectMail</li>
            <li>Open Gmail, click the <strong>gear icon</strong> (settings) → <strong>"See all settings"</strong></li>
            <li>Scroll to <strong>"Signature"</strong> section → click <strong>"Create new"</strong></li>
            <li>Click inside the signature editor and press <strong>Ctrl+V</strong> (or Cmd+V on Mac) to paste</li>
            <li>Scroll down and click <strong>"Save Changes"</strong></li>
          </ol>
        </div>

        {/* Outlook */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold">Outlook (Desktop & Web)</h3>
          </div>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside ml-2">
            <li>Click <strong>"Copy Signature"</strong> on ConnectMail</li>
            <li>In Outlook: <strong>File → Options → Mail → Signatures</strong> (Desktop) or <strong>Settings → Compose and reply</strong> (Web)</li>
            <li>Click <strong>"New"</strong>, give it a name</li>
            <li>Click inside the editor and <strong>paste</strong> (Ctrl+V / Cmd+V)</li>
            <li>Set as default for new messages → click <strong>"Save"</strong></li>
          </ol>
        </div>

        {/* Apple Mail */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-gray-700" />
            <h3 className="font-semibold">Apple Mail (Mac)</h3>
          </div>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside ml-2">
            <li>Click <strong>"Copy Signature"</strong> on ConnectMail</li>
            <li>Open Mail → <strong>Settings → Signatures</strong></li>
            <li>Click <strong>"+"</strong> to create a new signature</li>
            <li><strong>Uncheck</strong> "Always match my default message font"</li>
            <li>Click inside the signature area and <strong>paste</strong> (Cmd+V)</li>
            <li>Drag the signature to your email account on the left → close to save</li>
          </ol>
        </div>

        {/* iPhone/iPad */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Apple className="w-5 h-5 text-gray-700" />
            <h3 className="font-semibold">iPhone / iPad (Gmail App)</h3>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> The Gmail mobile app only supports plain-text signatures set within the app. To use a rich HTML signature (with images, colors, and links), set it up via <strong>desktop Gmail</strong> (mail.google.com) — it will automatically apply to emails sent from your phone.
            </p>
          </div>
          <p className="text-sm font-medium text-foreground ml-2">Option A: Set up via Desktop Gmail (Recommended)</p>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside ml-4">
            <li>On your computer, go to <strong>mail.google.com</strong></li>
            <li>Click the <strong>gear icon</strong> → <strong>"See all settings"</strong></li>
            <li>Scroll to <strong>Signature</strong> → click <strong>"Create new"</strong></li>
            <li>Click <strong>"Copy Signature"</strong> on ConnectMail and <strong>paste</strong> (Ctrl+V / Cmd+V) into Gmail</li>
            <li>Click <strong>"Save Changes"</strong> at the bottom of Gmail settings</li>
            <li>Your rich signature will now appear on emails sent from the Gmail app on your phone</li>
          </ol>
          <p className="text-sm font-medium text-foreground ml-2 mt-3">Option B: Plain-text signature in Gmail App</p>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside ml-4">
            <li>Open the <strong>Gmail app</strong> on your phone</li>
            <li>Tap the <strong>hamburger menu</strong> (three lines) → <strong>Settings</strong></li>
            <li>Select your email account</li>
            <li>Tap <strong>"Mobile Signature"</strong></li>
            <li>Type your signature text (plain text only — no images or formatting)</li>
            <li>Tap <strong>"Done"</strong> or <strong>"Save"</strong></li>
          </ol>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-800">
            <strong>💡 Pro Tip:</strong> After pasting, send a test email to yourself to make sure everything looks correct. The QR codes and Call Now buttons should be fully functional!
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ==================== QR CARD TAB (Digital Business Card + QR) ====================
const QRCardTab = ({ user, signatures }) => {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardUrl = `${window.location.origin}/card/${user?.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=ffffff&color=0f172a&qzone=1&data=${encodeURIComponent(cardUrl)}`;

  useEffect(() => {
    api.getBusinessCard().then(d => setCardData(d)).catch(() => setCardData(null)).finally(() => setLoading(false));
  }, []);

  const card = cardData?.card;

  const handleSaveContact = () => {
    const vcard = cardData?.vcard_data || `BEGIN:VCARD\nVERSION:3.0\nFN:${card?.full_name}\nEND:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${card?.full_name?.replace(/\s+/g, '_') || 'contact'}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
      {/* Digital Business Card */}
      <div className="w-full max-w-sm mx-auto lg:mx-0">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <QrCode className="w-5 h-5 text-primary" />
          Your Digital Business Card
        </h2>
        {loading ? (
          <div className="h-96 bg-muted rounded-2xl animate-pulse" />
        ) : (
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100" data-testid="digital-business-card">
            {/* Dark header */}
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] p-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-white/20 flex-shrink-0 bg-white/10">
                  {card?.photo_url ? (
                    <img src={resolveImageUrl(card.photo_url)} alt={card.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-3xl font-bold text-white/80">{(card?.full_name || user?.full_name || 'U')[0]}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-white truncate">{card?.full_name || user?.full_name}</h2>
                  <p className="text-sm text-blue-300 font-medium">{card?.profession || user?.profession_name}</p>
                  <p className="text-sm text-white/60">{card?.company || user?.company}</p>
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="p-5 space-y-3">
              {(card?.phone || user?.phone) && (
                <a href={`tel:${card?.phone || user?.phone}`} className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors text-sm">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-blue-600" />
                  </div>
                  {card?.phone || user?.phone}
                </a>
              )}
              {(card?.email || user?.email) && (
                <a href={`mailto:${card?.email || user?.email}`} className="flex items-center gap-3 text-gray-700 hover:text-primary transition-colors text-sm">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-blue-600" />
                  </div>
                  {card?.email || user?.email}
                </a>
              )}
              {(card?.address?.city || user?.city) && (
                <div className="flex items-center gap-3 text-gray-700 text-sm">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  {[card?.address?.city || user?.city, card?.address?.state || user?.state].filter(Boolean).join(', ')}
                </div>
              )}
            </div>

            {/* QR Code section */}
            <div className="px-5 pb-4">
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 border border-gray-100">
                <img src={cardData?.qr_code_url || qrUrl} alt="QR Code" className="w-20 h-20 rounded-lg" data-testid="bcard-qr-sig" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Scan to Save Contact</p>
                  <p className="text-xs text-gray-500 mt-1">Opens vCard on your phone</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 space-y-2">
              <Button onClick={handleSaveContact} className="w-full bg-gradient-to-r from-[#0f172a] to-[#1e3a5f] hover:opacity-90 text-white font-semibold" data-testid="save-contact-bcard">
                <Download className="w-4 h-4 mr-2" />
                Save Contact to Phone
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Share & Actions panel */}
      <div className="w-full max-w-xs space-y-4">
        <h3 className="text-base font-semibold">Share Your Card</h3>
        <Card>
          <CardContent className="p-4 space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => { navigator.clipboard?.writeText(cardUrl); toast.success('Link copied!'); }}
              data-testid="copy-card-link-sig"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Business Card Link
            </Button>
            <a href={cardData?.qr_code_url || qrUrl} download="connectmail-business-card-qr.png" className="block">
              <Button variant="outline" className="w-full justify-start" data-testid="download-qr-sig">
                <Download className="w-4 h-4 mr-2" />
                Download QR Code
              </Button>
            </a>
            <a href="/business-card" className="block">
              <Button variant="outline" className="w-full justify-start" data-testid="view-full-card">
                <QrCode className="w-4 h-4 mr-2" />
                View Full Business Card Page
              </Button>
            </a>
          </CardContent>
        </Card>

        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <p className="text-xs font-semibold mb-2">Pro Tips</p>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              {[
                'Add QR code to printed business cards',
                'Include your card link in email signature',
                'Display QR at trade shows & events',
                'Share on social media as your profile link',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== INSTALL SIGNATURE TAB ====================
const InstallSignatureTab = ({ signatures }) => {
  const { user } = useAuth();
  const [activeClient, setActiveClient] = useState('gmail');
  const [copied, setCopied] = useState(false);
  const [exportedHtml, setExportedHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const latestSig = signatures[0];

  const isPremium = user?.is_premium === true;

  // Load fresh exported HTML for preview
  React.useEffect(() => {
    if (!latestSig?.id) return;
    setLoadingPreview(true);
    api.exportSignature(latestSig.id)
      .then(data => setExportedHtml(data.html))
      .catch(() => setExportedHtml(latestSig.html_content || ''))
      .finally(() => setLoadingPreview(false));
  }, [latestSig?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopySignature = async () => {
    if (!latestSig?.id) {
      toast.error('No signature found. Build one in the Builder tab first.');
      return;
    }
    try {
      // Use pre-loaded exported HTML, or fetch fresh
      const html = exportedHtml || (await api.exportSignature(latestSig.id)).html;

      // Try modern clipboard API first
      try {
        const blob = new Blob([html], { type: 'text/html' });
        const clipboardItem = new ClipboardItem({ 'text/html': blob });
        await navigator.clipboard.write([clipboardItem]);
      } catch {
        // Fallback: use hidden contenteditable div
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.contentEditable = 'true';
        document.body.appendChild(tempDiv);
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
      }

      setCopied(true);
      toast.success('Signature copied! Now paste it into your email client.');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Failed to copy signature. Try from the Builder tab.');
    }
  };

  const clients = [
    { id: 'gmail', label: 'Gmail', icon: Chrome, color: 'text-red-500' },
    { id: 'outlook', label: 'Outlook', icon: Monitor, color: 'text-blue-600' },
    { id: 'apple', label: 'Apple Mail', icon: Apple, color: 'text-gray-700' },
    { id: 'mobile', label: 'Mobile', icon: Phone, color: 'text-green-600' },
  ];

  const steps = {
    gmail: [
      { step: 1, title: 'Copy your signature', desc: 'Click the "Copy Signature" button above' },
      { step: 2, title: 'Open Gmail Settings', desc: 'Click the gear icon (⚙) in Gmail → "See all settings"' },
      { step: 3, title: 'Find the Signature section', desc: 'Scroll to the "Signature" section → click "Create new"' },
      { step: 4, title: 'Paste your signature', desc: 'Click inside the signature editor and press Ctrl+V (or Cmd+V on Mac)' },
      { step: 5, title: 'Save Changes', desc: 'Scroll to the bottom and click "Save Changes" — done!' },
    ],
    outlook: [
      { step: 1, title: 'Copy your signature', desc: 'Click the "Copy Signature" button above' },
      { step: 2, title: 'Open Outlook Signatures', desc: 'Desktop: File → Options → Mail → Signatures  |  Web: Settings → Compose and reply' },
      { step: 3, title: 'Create a new signature', desc: 'Click "New", give your signature a name' },
      { step: 4, title: 'Paste your signature', desc: 'Click inside the editor and paste (Ctrl+V / Cmd+V)' },
      { step: 5, title: 'Set as default & save', desc: 'Choose it as default for new messages → click "Save"' },
    ],
    apple: [
      { step: 1, title: 'Copy your signature', desc: 'Click the "Copy Signature" button above' },
      { step: 2, title: 'Open Mail Preferences', desc: 'Open Mail → Settings (Cmd+,) → click "Signatures"' },
      { step: 3, title: 'Create a new signature', desc: 'Click "+" to create a new signature, give it a name' },
      { step: 4, title: 'Uncheck font matching', desc: 'Uncheck "Always match my default message font" (important!)' },
      { step: 5, title: 'Paste & save', desc: 'Click inside the signature area, paste (Cmd+V), then drag to your email account' },
    ],
    mobile: [
      { step: 1, title: 'Set up on desktop first', desc: 'Rich HTML signatures must be set up via desktop Gmail or Outlook — they then sync to mobile' },
      { step: 2, title: 'Gmail Desktop → Mobile', desc: 'Set signature at mail.google.com → it automatically shows on the Gmail mobile app' },
      { step: 3, title: 'Outlook Mobile', desc: 'Signatures created in Outlook desktop or web automatically sync to the mobile app' },
      { step: 4, title: 'Plain text (mobile-only)', desc: 'Gmail App: Menu → Settings → Account → Mobile Signature (plain text only, no images)' },
    ],
  };

  return (
    <div className="mx-auto space-y-6" data-testid="install-tab-content">
      {/* Header + copy button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Install Your Email Signature
          </h2>
          <p className="text-sm text-muted-foreground">One-click copy, then paste into any email client</p>
        </div>
        <Button
          onClick={handleCopySignature}
          size="lg"
          className={`flex-shrink-0 px-6 ${copied ? 'bg-green-600 hover:bg-green-700' : ''}`}
          data-testid="copy-sig-btn"
        >
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? 'Copied!' : 'Copy Signature'}
        </Button>
      </div>

      {/* Signature Preview */}
      {latestSig?.id ? (
        <div className="border-b border-border pb-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Your Current Signature — This Is What Gets Copied</p>
          {loadingPreview ? (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading signature preview...</span>
            </div>
          ) : (
            <div className="signature-preview" dangerouslySetInnerHTML={{ __html: exportedHtml || latestSig.html_content }} />
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            <strong>No signature yet.</strong> Go to the Builder tab to create your first professional email signature.
          </p>
        </div>
      )}

      {/* How it works */}
      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-4">
        <p className="text-sm text-green-800 dark:text-green-300">
          <strong>How it works:</strong> Click "Copy Signature" above, then follow the steps below to paste it into your email client. It pastes as a fully formatted signature — no coding needed.
        </p>
      </div>

      {/* Email client tabs */}
      <div className="flex gap-2 border-b border-border pb-0">
        {clients.map(c => (
          <button
            key={c.id}
            onClick={() => setActiveClient(c.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
              activeClient === c.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`client-tab-${c.id}`}
          >
            <c.icon className={`w-4 h-4 ${c.color}`} />
            {c.label}
          </button>
        ))}
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps[activeClient].map((s) => (
          <div key={s.step} className="flex gap-4 p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:bg-muted/20 transition-all">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm flex-shrink-0">
              {s.step}
            </div>
            <div>
              <p className="text-sm font-semibold">{s.title}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <strong>Pro Tip:</strong> After pasting, send a test email to yourself to confirm everything looks correct. QR codes and call buttons should be fully functional!
        </p>
      </div>
      {showActivate && <ActivateModal feature="Install Signature" onClose={() => setShowActivate(false)} />}
    </div>
  );
};

export const SignatureBuilder = () => {
  const { user, refreshProfile } = useAuth();
  const [formData, setFormData] = useState({
    template_style: 'executive',
    color_scheme: 'navy',
    include_photo: true,
    include_social: true,
    tagline: '',
    photo_url: '',
    custom_message: '',
    custom_message_color: '',
    disclaimer: '',
    font_family: '',
    text_color: '',
    banner_url: ''
  });
  const [signatures, setSignatures] = useState([]);
  const [currentSignature, setCurrentSignature] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'builder');
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [marketplaceBlink, setMarketplaceBlink] = useState(false);
  const [cropState, setCropState] = useState({ open: false, src: null });
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailConnectedAt, setGmailConnectedAt] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [pushingGmail, setPushingGmail] = useState(false);
  const [copyBlink, setCopyBlink] = useState(false);
  const [chooseBlinkActive, setChooseBlinkActive] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const photoSetByUser = React.useRef(false);

  const isPremium = user?.is_premium === true;
  const requirePremium = (action) => {
    return true;
  };
  const previewRef = useRef(null);
  const previewContainerRef = useRef(null);

  // Handle Gmail OAuth callback redirect + copy blink trigger
  useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      toast.success('Gmail connected! Your signature will auto-update when ads change.');
      setGmailConnected(true);
      setActiveTab('settings');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (gmailParam === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`Gmail connection failed: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Handle blink=copy from signature update popup
    const blinkParam = searchParams.get('blink');
    if (blinkParam === 'copy') {
      setCopyBlink(true);
      setActiveTab('builder');
      setTimeout(() => setCopyBlink(false), 15000);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (blinkParam === 'choose') {
      setActiveTab('library');
      setChooseBlinkActive(true);
      setTimeout(() => setChooseBlinkActive(false), 20000);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch Gmail connection status
  useEffect(() => {
    api.gmailStatus().then(data => {
      setGmailConnected(data.connected || false);
      setGmailConnectedAt(data.connected_at || null);
    }).catch(() => {});
  }, []);

  // JavaScript-based sticky for preview pane (desktop only)
  useEffect(() => {
    const container = previewContainerRef.current;
    const preview = previewRef.current;
    if (!container || !preview) return;

    const handleScroll = () => {
      // Only apply sticky on large screens
      if (window.innerWidth < 1024) {
        preview.style.position = 'relative';
        preview.style.top = 'auto';
        preview.style.bottom = 'auto';
        preview.style.width = '100%';
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const previewHeight = preview.offsetHeight;
      const topOffset = 24;

      if (containerRect.top <= topOffset && containerRect.bottom > previewHeight + topOffset) {
        // Stick to top
        preview.style.position = 'fixed';
        preview.style.top = `${topOffset}px`;
        preview.style.width = `${container.offsetWidth}px`;
      } else if (containerRect.bottom <= previewHeight + topOffset) {
        // Pin to bottom of container
        preview.style.position = 'absolute';
        preview.style.top = 'auto';
        preview.style.bottom = '0';
        preview.style.width = '100%';
      } else {
        // Normal position
        preview.style.position = 'relative';
        preview.style.top = 'auto';
        preview.style.width = '100%';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [activeTab, currentSignature]);
  const [marketplaceSignatureId, setMarketplaceSignatureId] = useState(null);
  const [editingSig, setEditingSig] = useState(null);
  const [editForm, setEditForm] = useState({ custom_message: '', disclaimer: '', tagline: '', font_family: '', text_color: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [livePreviewHtml, setLivePreviewHtml] = useState('');
  const [livePreviewLoading, setLivePreviewLoading] = useState(false);
  const previewDebounceRef = React.useRef(null);

  // Custom signature state
  const [customMode, setCustomMode] = useState('visual'); // 'visual' or 'html'
  const [customHtml, setCustomHtml] = useState('');
  const [customBlocks, setCustomBlocks] = useState([
    { id: '1', type: 'text', content: 'Your Name', style: 'font-size:22px;font-weight:bold;color:#1e3a5f;' },
    { id: '2', type: 'text', content: 'Your Title | Company', style: 'font-size:13px;color:#3b82f6;' },
    { id: '3', type: 'divider', style: 'border-top:2px solid #3b82f6;width:60px;margin:8px 0;' },
    { id: '4', type: 'text', content: 'phone@email.com | (555) 123-4567', style: 'font-size:12px;color:#555;' }
  ]);
  const [savingCustom, setSavingCustom] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  // Install Wizard state — auto-launched after signature generation, and
  // accessible manually via the "Install Signature" button.
  const [installWizardOpen, setInstallWizardOpen] = useState(false);
  const [installWizardSigId, setInstallWizardSigId] = useState(null);
  // Profile editing state (for Settings tab)
  const [profileData, setProfileData] = useState({});
  const [professions, setProfessions] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState({ logo: false, photo: false });

  useEffect(() => {
    fetchSignatures();
    fetchPendingApprovalCount();
    if (user?.marketplace_signature_id) {
      setMarketplaceSignatureId(user.marketplace_signature_id);
    }
    if (user && !photoSetByUser.current) {
      const photo = user.profile_photo_url || user.logo_url || '';
      if (photo && (photo.includes('/api/images/') || photo.includes('/api/uploads/'))) {
        setFormData(prev => ({ ...prev, photo_url: photo }));
      }
    }
    // Fetch founding member status for premium template gating
    if (user) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/founding-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.json())
        .then(data => setFoundingStatus(data))
        .catch(() => {});
    }
    // Initialize profile data from user
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        website: user.website || '',
        profession_id: user.profession_id || '',
        custom_profession: user.custom_profession || '',
        company: user.company || '',
        position_title: user.position_title || '',
        custom_position: user.custom_position || '',
        street_address: user.street_address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
        linkedin_url: user.linkedin_url || '',
        twitter_url: user.twitter_url || '',
        facebook_url: user.facebook_url || '',
        instagram_url: user.instagram_url || '',
        logo_url: user.logo_url || '',
        profile_photo_url: user.profile_photo_url || '',
      });
    }
    // Fetch professions
    api.getProfessions?.().then(data => setProfessions(data?.professions || data || [])).catch(() => {});
  }, [user]);

  const fetchSignatures = async () => {
    try {
      const data = await api.getMySignatures();
      const sigs = data.signatures || [];
      setSignatures(sigs);
      if (!currentSignature && sigs.length > 0) {
        setCurrentSignature(sigs[0]);
      }
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
    }
  };

  const fetchPendingApprovalCount = async () => {
    try {
      const data = await api.getPendingApprovals();
      setPendingApprovalCount((data.pending || []).length);
    } catch {}
  };

  // Live preview: re-generate when font, color, template, or scheme changes
  useEffect(() => {
    if (!user) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      setLivePreviewLoading(true);
      try {
        const photoUrl = formData.photo_url || user?.profile_photo_url || user?.logo_url;
        const data = await api.quickPreviewSignature({
          ...formData,
          photo_url: resolveImageUrl(photoUrl)
        });
        setLivePreviewHtml(data.html);
      } catch (e) {
        // silent fail — live preview is best-effort
      } finally {
        setLivePreviewLoading(false);
      }
    }, 500);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.template_style, formData.color_scheme, formData.font_family, formData.text_color, formData.tagline, formData.custom_message, formData.custom_message_color, formData.disclaimer, formData.photo_url, formData.banner_url]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropState({ open: true, src: reader.result });
    reader.readAsDataURL(file);
  };

  const handlePhotoCropDone = async (croppedBlob) => {
    setCropState({ open: false, src: null });
    const file = new File([croppedBlob], 'cropped_photo.jpg', { type: 'image/jpeg' });
    setUploading(true);
    try {
      const result = await api.uploadImage(file);
      const relativeUrl = result.url;
      photoSetByUser.current = true;
      setFormData(prev => ({ ...prev, photo_url: relativeUrl }));
      await api.updateProfile({ profile_photo_url: relativeUrl });
      toast.success('Picture uploaded and saved!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };


  // Profile editing handlers for Settings tab
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.updateProfile(profileData);
      if (refreshProfile) refreshProfile();
      toast.success('Profile updated! Click "Apply Changes" to update your signature.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update profile');
    } finally { setSavingProfile(false); }
  };

  const handleProfileImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingProfile(prev => ({ ...prev, [field]: true }));
    try {
      const result = await api.uploadImage(file);
      const url = result.url;
      setProfileData(prev => ({ ...prev, [field]: url }));
      await api.updateProfile({ [field]: url });
      if (field === 'profile_photo_url') {
        photoSetByUser.current = true;
        setFormData(prev => ({ ...prev, photo_url: url }));
      }
      if (refreshProfile) refreshProfile();
      toast.success(`${field === 'logo_url' ? 'Logo' : 'Photo'} uploaded!`);
    } catch { toast.error('Upload failed'); }
    setUploadingProfile(prev => ({ ...prev, [field]: false }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const photoUrl = formData.photo_url || user?.profile_photo_url || user?.logo_url;
      const response = await api.generateSignature({
        ...formData,
        photo_url: resolveImageUrl(photoUrl)
      });
      setCurrentSignature(response);
      setLivePreviewHtml('');
      setSignatures(prev => [response, ...prev]);
      // Auto-launch the install wizard for first-time signature generation.
      // The wizard's job is to walk the user through pasting their new
      // signature into Gmail/Outlook/Apple Mail/etc in under 2 minutes.
      setInstallWizardSigId(response?.id);
      setInstallWizardOpen(true);
      toast.success('Signature generated! Set it for marketplace to start earning.', {
        action: {
          label: 'Go to Marketplace',
          onClick: () => setActiveTab('marketplace')
        },
        duration: 5000
      });
      // Show marketing upsell after a short delay
      setTimeout(() => {
        toast(
          'Expand your reach for just $20/month! Open the door to passive income by advertising your services in other professionals\' email signatures.',
          {
            duration: 12000,
            action: {
              label: 'Learn More',
              onClick: () => setActiveTab('marketplace')
            },
            style: { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534' }
          }
        );
      }, 3000);
      setMarketplaceBlink(true);
      setTimeout(() => setMarketplaceBlink(false), 10000);
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error('Failed to generate signature. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Delete a signature from library
  const handleDeleteSignature = async (e, sigId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this signature? This cannot be undone.')) return;
    try {
      await api.deleteSignature(sigId);
      setSignatures(prev => prev.filter(s => s.id !== sigId));
      if (currentSignature?.id === sigId) {
        setCurrentSignature(null);
        setShowAdPreview(false);
      }
      toast.success('Signature deleted');
    } catch (error) {
      toast.error('Failed to delete signature');
    }
  };

  // Copy rendered signature as rich text (for pasting into Gmail/Apple Mail)
  const handleCopySignature = async () => {
    if (!currentSignature) return;
    if (!requirePremium('Copy Signature')) return;
    try {
      const data = await api.exportSignature(currentSignature.id);
      const html = data.html;
      
      // Copy as rich text so email clients render it visually
      const blob = new Blob([html], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      
      setCopySuccess(true);
      toast.success('Signature copied! Paste directly into your email signature settings.');
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (error) {
      // Fallback: use a hidden contenteditable div to copy rich text
      try {
        const data = await api.exportSignature(currentSignature.id);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = data.html;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        tempDiv.contentEditable = 'true';
        document.body.appendChild(tempDiv);
        
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
        
        setCopySuccess(true);
        toast.success('Signature copied! Paste directly into your email signature settings.');
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (fallbackError) {
        toast.error('Failed to copy signature');
      }
    }
  };

  // Copy raw HTML code (for developers/advanced users)
  const handleCopyHtmlCode = async () => {
    if (!currentSignature) return;
    if (!requirePremium('Copy HTML Code')) return;
    try {
      const data = await api.exportSignature(currentSignature.id);
      await navigator.clipboard.writeText(data.html);
      toast.success('HTML code copied to clipboard.');
    } catch (error) {
      toast.error('Failed to copy HTML code');
    }
  };

  const handleDownload = async () => {
    if (!currentSignature) return;
    if (!requirePremium('Download Signature')) return;
    try {
      const data = await api.exportSignature(currentSignature.id);
      const blob = new Blob([data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connectmail-signature.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Signature downloaded!');
    } catch (error) {
      toast.error('Failed to download signature');
    }
  };

  const [sendingPreview, setSendingPreview] = useState(false);
  const handleSendPreviewEmail = async () => {
    if (!currentSignature) return;
    setSendingPreview(true);
    try {
      const data = await api.sendPreviewEmail(currentSignature?.id);
      if (data.success) {
        toast.success(data.message || 'Preview email sent! Check your inbox.');
      } else {
        const debugInfo = data.debug_key_prefix ? ` [Key: ${data.debug_key_prefix}]` : '';
        toast.error((data.message || 'Failed to send preview email') + debugInfo);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send preview email');
    } finally {
      setSendingPreview(false);
    }
  };

  // Preview signature with sample ads filled in
  const [showAdPreview, setShowAdPreview] = useState(false);
  const [adPreviewHtml, setAdPreviewHtml] = useState('');
  const [previewAds, setPreviewAds] = useState([]);
  const handlePreviewWithAds = async () => {
    if (!currentSignature) return;
    try {
      const data = await api.previewWithAds(currentSignature.id);
      setAdPreviewHtml(data.html);
      setPreviewAds(data.ads || []);
      setShowAdPreview(true);
      const realCount = (data.ads || []).filter(a => a.is_real).length;
      const sampleCount = (data.ads || []).filter(a => a.is_sample).length;
      const msg = realCount > 0
        ? `Preview loaded with ${realCount} active ad${realCount > 1 ? 's' : ''} and ${sampleCount} sample ad${sampleCount !== 1 ? 's' : ''}`
        : `Preview loaded with ${data.slots_filled} sample ads`;
      toast.success(msg);
    } catch (error) {
      toast.error('Failed to load ad preview');
    }
  };

  const selectExistingSignature = async (sig) => {
    setCurrentSignature(sig);
    setLivePreviewHtml('');
    setFormData({
      template_style: sig.template_style || 'executive',
      color_scheme: sig.color_scheme || 'navy',
      include_photo: true,
      include_social: true,
      tagline: sig.tagline || '',
      photo_url: '',
      custom_message: sig.custom_message || '',
      disclaimer: sig.disclaimer || '',
      font_family: sig.font_family || '',
      text_color: sig.text_color || '',
      banner_url: sig.banner_url || ''
    });
    setActiveTab('builder');
  };

  const handleGmailConnect = async () => {
    setGmailLoading(true);
    try {
      const data = await api.gmailConnect();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start Gmail connection');
      setGmailLoading(false);
    }
  };

  const handleGmailDisconnect = async () => {
    try {
      await api.gmailDisconnect();
      setGmailConnected(false);
      setGmailConnectedAt(null);
      toast.success('Gmail disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    }
  };

  const handlePushToGmail = async () => {
    setPushingGmail(true);
    try {
      const sigId = currentSignature?.id || null;
      const data = await api.pushToGmail(sigId);
      toast.success(data.message || 'Signature pushed to Gmail!');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to push signature to Gmail');
    } finally {
      setPushingGmail(false);
    }
  };

  const handleSetMarketplaceSignature = async (signatureId) => {
    try {
      await api.setMarketplaceSignature(signatureId);
      setMarketplaceSignatureId(signatureId);
      if (refreshProfile) await refreshProfile();
      toast.success('Signature is live in the marketplace! Head to Marketplace to manage your ad slots.', {
        action: {
          label: 'Open Marketplace',
          onClick: () => setActiveTab('marketplace')
        }
      });
      setMarketplaceBlink(true);
      setTimeout(() => setMarketplaceBlink(false), 10000);
    } catch (error) {
      toast.error('Failed to set marketplace signature');
    }
  };

  const handleRemoveMarketplaceSignature = async () => {
    try {
      await api.removeMarketplaceSignature();
      setMarketplaceSignatureId(null);
      if (refreshProfile) await refreshProfile();
      toast.success('Marketplace signature removed');
    } catch (error) {
      toast.error('Failed to remove marketplace signature');
    }
  };

  // Custom signature functions
  const addBlock = (type) => {
    const id = Date.now().toString();
    let newBlock;
    switch (type) {
      case 'text':
        newBlock = { id, type: 'text', content: 'Edit this text', style: 'font-size:14px;color:#333;' };
        break;
      case 'image':
        newBlock = { id, type: 'image', src: '', alt: 'Image', link: '', style: 'max-width:120px;border-radius:8px;' };
        break;
      case 'divider':
        newBlock = { id, type: 'divider', style: 'border-top:2px solid #3b82f6;width:100%;margin:8px 0;' };
        break;
      default:
        return;
    }
    setCustomBlocks(prev => [...prev, newBlock]);
  };

  const updateBlock = (id, updates) => {
    setCustomBlocks(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  const removeBlock = (id) => {
    setCustomBlocks(prev => prev.filter(b => b.id !== id));
  };

  const moveBlock = (fromIdx, toIdx) => {
    setCustomBlocks(prev => {
      const items = [...prev];
      const [moved] = items.splice(fromIdx, 1);
      items.splice(toIdx, 0, moved);
      return items;
    });
  };

  const blocksToHtml = () => {
    return customBlocks.map(b => {
      if (b.type === 'text') {
        return `<p style="${b.style}margin:4px 0;">${b.content}</p>`;
      } else if (b.type === 'image') {
        const resolvedSrc = resolveImageUrl(b.src);
        const img = `<img src="${resolvedSrc}" alt="${b.alt}" style="${b.style}" />`;
        return b.link ? `<a href="${b.link}" target="_blank">${img}</a>` : img;
      } else if (b.type === 'divider') {
        return `<div style="${b.style}"></div>`;
      }
      return '';
    }).join('\n');
  };

  const handleUploadBlockImage = async (blockId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await api.uploadImage(file);
      updateBlock(blockId, { src: result.url });
      toast.success('Image uploaded!');
    } catch {
      toast.error('Upload failed');
    }
  };

  const saveCustomSignature = async () => {
    setSavingCustom(true);
    try {
      const html = customMode === 'html' ? customHtml : blocksToHtml();
      if (!html.trim()) {
        toast.error('Signature content is empty');
        return;
      }
      const response = await api.saveCustomSignature({ html_content: html, name: 'Custom Signature' });
      setCurrentSignature(response);
      setSignatures(prev => [response, ...prev]);
      toast.success('Custom signature saved!');
    } catch (error) {
      toast.error('Failed to save custom signature');
    } finally {
      setSavingCustom(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="signature-builder">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight">Signature Builder</h1>
            <p className="text-muted-foreground mt-1">
              Create stunning AI-powered email signatures
            </p>
          </div>
          <IntegrationInstructions />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full max-w-4xl overflow-x-auto">
            <TabsTrigger value="builder" data-testid="builder-tab">
              <Wand2 className="w-4 h-4 mr-2" />
              Builder
            </TabsTrigger>
            <TabsTrigger value="custom" data-testid="custom-tab">
              <Type className="w-4 h-4 mr-2" />
              Custom
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="library-tab">
              <LayoutIcon className="w-4 h-4 mr-2" />
              Library ({signatures.length})
            </TabsTrigger>
            <TabsTrigger value="marketplace" data-testid="marketplace-tab" className={marketplaceBlink ? 'animate-pulse bg-green-100 text-green-700 ring-2 ring-green-400' : ''} onClick={() => setMarketplaceBlink(false)}>
              <ShoppingBag className={`w-4 h-4 mr-2 ${marketplaceBlink ? 'text-green-600' : ''}`} />
              Marketplace
              {marketplaceBlink && (
                <span className="ml-1.5 relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="approvals" data-testid="approvals-tab" className="relative">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approvals
              {pendingApprovalCount > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {pendingApprovalCount > 9 ? '9+' : pendingApprovalCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="qrcode" data-testid="qrcode-tab">
              <QrCode className="w-4 h-4 mr-2" />
              QR Code
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Controls */}
              <div className="space-y-6">
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Palette className="w-5 h-5" />
                      Design Your Signature
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Template Style */}
                    <div className="space-y-3">
                      <Label>Signature Style</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TEMPLATE_STYLES.map((style) => {
                          const canAccess = !style.premium || user?.is_premium || foundingStatus?.is_founding_member;
                          return (
                          <button
                            key={style.id}
                            onClick={() => {
                              if (style.premium && !canAccess) {
                                toast.error('Premium template — only Founding Members (first 1,000) or Pro subscribers can use this.', {
                                  action: { label: 'Upgrade to Pro', onClick: () => window.location.href = '/settings?tab=billing' },
                                  duration: 5000
                                });
                                return;
                              }
                              setFormData(prev => ({ ...prev, template_style: style.id }));
                            }}
                            className={`relative p-3 rounded-xl border-2 text-left transition-all overflow-hidden ${
                              formData.template_style === style.id
                                ? 'border-accent ring-2 ring-accent/20'
                                : 'border-border hover:border-accent/50'
                            } ${style.premium && !canAccess ? 'opacity-70' : ''}`}
                            data-testid={`style-${style.id}`}
                          >
                            <div 
                              className="absolute inset-0 opacity-10"
                              style={{background: style.preview}}
                            />
                            <div className="relative flex items-center gap-2">
                              <p className="font-semibold text-sm">{style.name}</p>
                              {style.badge && (
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                                  style.premium ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'
                                }`}>{style.badge}</span>
                              )}
                              {style.premium && !canAccess && (
                                <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground relative">{style.description}</p>
                          </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Color Scheme */}
                    <div className="space-y-3">
                      <Label>Color Scheme</Label>
                      <div className="flex flex-wrap gap-2">
                        {COLOR_SCHEMES.map((color) => (
                          <button
                            key={color.id}
                            onClick={() => setFormData(prev => ({ ...prev, color_scheme: color.id }))}
                            className={`group relative w-12 h-12 rounded-xl border-2 transition-all ${
                              formData.color_scheme === color.id
                                ? 'border-foreground scale-110 shadow-lg'
                                : 'border-transparent hover:scale-105'
                            }`}
                            style={{ background: `linear-gradient(135deg, ${color.color} 0%, ${color.accent} 100%)` }}
                            title={color.name}
                            data-testid={`color-${color.id}`}
                          >
                            {formData.color_scheme === color.id && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Check className="w-5 h-5 text-white drop-shadow-lg" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Photo Upload */}
                    <div className="space-y-3">
                      <Label>Profile Picture / Company Picture</Label>
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/50">
                          {formData.photo_url ? (
                            <img 
                              src={resolveImageUrl(formData.photo_url)} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : user?.profile_photo_url ? (
                            <img 
                              src={resolveImageUrl(user.profile_photo_url)} 
                              alt="Picture" 
                              className="w-full h-full object-cover"
                            />
                          ) : user?.logo_url ? (
                            <img 
                              src={resolveImageUrl(user.logo_url)} 
                              alt="Picture" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Upload className="w-6 h-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileUpload(e)}
                            className="hidden"
                            id="photo-upload"
                          />
                          <label htmlFor="photo-upload">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="cursor-pointer" 
                              asChild
                              disabled={uploading}
                            >
                              <span>
                                {uploading ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload Image
                                  </>
                                )}
                              </span>
                            </Button>
                          </label>
                          <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 2MB</p>
                        </div>
                      </div>
                    </div>

                    {/* Tagline */}
                    <div className="space-y-2">
                      <Label htmlFor="tagline">Professional Tagline (Optional)</Label>
                      <Input
                        id="tagline"
                        placeholder="Your trusted partner in real estate..."
                        value={formData.tagline}
                        onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                        data-testid="tagline-input"
                      />
                    </div>

                    {/* Font Family */}
                    <div className="space-y-2">
                      <Label className="text-sm">Font Family</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILIES.map((font) => (
                          <button
                            key={font.id}
                            onClick={() => setFormData(prev => ({ ...prev, font_family: font.value }))}
                            className={`p-2 rounded-lg border text-left transition-all ${formData.font_family === font.value ? 'border-accent ring-2 ring-accent/20 bg-accent/5' : 'border-border hover:border-accent/50'}`}
                            style={{ fontFamily: font.value || 'inherit' }}
                            data-testid={`font-${font.id}`}
                          >
                            <span className="text-xs font-medium">{font.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Text Color */}
                    <div className="space-y-2">
                      <Label className="text-sm">Text Color</Label>
                      <div className="flex flex-wrap gap-2">
                        {TEXT_COLORS.map((color) => (
                          <button
                            key={color.id}
                            onClick={() => setFormData(prev => ({ ...prev, text_color: color.value }))}
                            className={`relative w-8 h-8 rounded-lg border-2 transition-all ${formData.text_color === color.value ? 'border-foreground scale-110 shadow-lg' : 'border-transparent hover:scale-105'}`}
                            style={{ backgroundColor: color.value || '#f3f4f6' }}
                            title={color.name}
                            data-testid={`text-color-${color.id}`}
                          >
                            {formData.text_color === color.value && <div className="absolute inset-0 flex items-center justify-center"><Check className="w-3 h-3 drop-shadow-lg" style={{color: color.value ? '#fff' : '#333'}} /></div>}
                            {color.id === 'default' && <span className="absolute inset-0 flex items-center justify-center text-[9px] text-gray-500">Auto</span>}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Message */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">Custom Message (Optional)</Label>
                      <textarea
                        placeholder="Add a promotional message or note..."
                        value={formData.custom_message}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_message: e.target.value }))}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[60px] resize-y"
                        data-testid="custom-message-input"
                      />
                      {formData.custom_message && (
                        <div className="flex items-center gap-2 pt-1">
                          <Label className="text-xs text-muted-foreground">Color:</Label>
                          <div className="flex gap-1.5">
                            {['#333333', '#1e3a5f', '#065f46', '#7f1d1d', '#4c1d95', '#c2410c'].map(c => (
                              <button key={c} onClick={() => setFormData(prev => ({ ...prev, custom_message_color: c }))}
                                className={`w-5 h-5 rounded-full border-2 ${formData.custom_message_color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                                style={{ background: c }} data-testid={`msg-color-${c.replace('#', '')}`} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Disclaimer */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">Legal Disclaimer (Optional)</Label>
                      <textarea
                        placeholder="CONFIDENTIALITY NOTICE: ..."
                        value={formData.disclaimer}
                        onChange={(e) => setFormData(prev => ({ ...prev, disclaimer: e.target.value }))}
                        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[50px] resize-y"
                        data-testid="disclaimer-input"
                      />
                    </div>

                    {/* Visual Block Image — only for premium templates */}
                    {['premium_corporate', 'premium_visual', 'premium_marketing'].includes(formData.template_style) && (
                      <div className="space-y-3 p-4 rounded-xl border-2 border-amber-200/80 bg-gradient-to-b from-amber-50/60 to-white" data-testid="banner-upload-section">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-bold flex items-center gap-2">
                            <Image className="w-4 h-4 text-amber-600" />
                            Animated Profile Photo
                          </Label>
                          <span className="text-[9px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">PREMIUM</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Your profile photo animates with a tile-reveal effect when you generate the signature. Upload a custom image to override.</p>
                        <div className="flex items-center gap-4">
                          <div className="w-[120px] h-[120px] rounded-lg border-2 border-dashed border-amber-300 flex items-center justify-center overflow-hidden bg-white flex-shrink-0">
                            {formData.banner_url ? (
                              <img
                                src={resolveImageUrl(formData.banner_url)}
                                alt="Visual block"
                                className="w-full h-full object-cover rounded"
                              />
                            ) : (
                              <span className="text-[10px] text-center text-muted-foreground px-2">Profile photo will be used</span>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 flex-1">
                            <input
                              type="file"
                              accept="image/*,.gif"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                e.target.value = '';
                                if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
                                setUploadingBanner(true);
                                try {
                                  const result = await api.uploadImage(file);
                                  setFormData(prev => ({ ...prev, banner_url: result.url }));
                                  toast.success('Visual image uploaded!');
                                } catch { toast.error('Failed to upload image'); }
                                setUploadingBanner(false);
                              }}
                              className="hidden"
                              id="banner-upload"
                              data-testid="banner-upload-input"
                            />
                            <label htmlFor="banner-upload">
                              <Button variant="outline" size="sm" className="w-full cursor-pointer" asChild disabled={uploadingBanner}>
                                <span>
                                  {uploadingBanner ? (
                                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading...</>
                                  ) : (
                                    <><Upload className="w-3.5 h-3.5 mr-1.5" />Upload Image / GIF</>
                                  )}
                                </span>
                              </Button>
                            </label>
                            {formData.banner_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-red-500 h-7 w-full"
                                onClick={() => setFormData(prev => ({ ...prev, banner_url: '' }))}
                                data-testid="remove-banner-btn"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Remove (use profile photo)
                              </Button>
                            )}
                            <p className="text-[10px] text-muted-foreground">Supports PNG, JPG, GIF. For animated content, upload a GIF.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="w-full h-12 text-base"
                      data-testid="generate-btn"
                    >
                      {generating ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Creating Your Signature...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-5 h-5 mr-2" />
                          Generate Signature
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Auto-Install Signature */}
                <Card className="border-border/50" data-testid="auto-install-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Auto-Install Signature
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Push your signature directly to your email — no copy-paste needed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Gmail */}
                    <div className={`flex items-center justify-between p-3 rounded-lg border ${gmailConnected ? 'border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800' : 'border-border'}`}>
                      <div className="flex items-center gap-3">
                        <Chrome className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Gmail</p>
                          <p className="text-xs text-muted-foreground">
                            {gmailConnected ? 'Connected — auto-syncs when ads change' : 'One-click auto-install via Google'}
                          </p>
                        </div>
                      </div>
                      {gmailConnected ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handlePushToGmail} disabled={pushingGmail || !signatures.length} data-testid="builder-push-gmail-btn">
                            {pushingGmail ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Push Now'}
                          </Button>
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        </div>
                      ) : (
                        <Button size="sm" className="h-8 text-xs bg-red-600 hover:bg-red-700" onClick={handleGmailConnect} disabled={gmailLoading} data-testid="builder-gmail-connect-btn">
                          {gmailLoading ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Chrome className="w-3 h-3 mr-1" />}
                          Connect
                        </Button>
                      )}
                    </div>

                    {/* Outlook/Hotmail */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Monitor className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Outlook / Hotmail</p>
                          <p className="text-xs text-muted-foreground">Copy & paste — Microsoft doesn't offer an auto-install API</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className={`h-8 text-xs ${copyBlink ? 'animate-pulse ring-2 ring-green-400 bg-green-100 text-green-700 border-green-400' : ''}`} onClick={() => { handleCopySignature(); setCopyBlink(false); }} disabled={!currentSignature} data-testid="builder-outlook-copy-btn">
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>

                    {/* Yahoo */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-purple-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Yahoo Mail</p>
                          <p className="text-xs text-muted-foreground">Copy & paste — Yahoo doesn't offer an auto-install API</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className={`h-8 text-xs ${copyBlink ? 'animate-pulse ring-2 ring-green-400 bg-green-100 text-green-700 border-green-400' : ''}`} onClick={() => { handleCopySignature(); setCopyBlink(false); }} disabled={!currentSignature} data-testid="builder-yahoo-copy-btn">
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>

                    {/* Apple Mail */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <div className="flex items-center gap-3">
                        <Apple className="w-5 h-5 text-gray-700 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">Apple Mail</p>
                          <p className="text-xs text-muted-foreground">Copy & paste — see Install tab for detailed steps</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className={`h-8 text-xs ${copyBlink ? 'animate-pulse ring-2 ring-green-400 bg-green-100 text-green-700 border-green-400' : ''}`} onClick={() => { handleCopySignature(); setCopyBlink(false); }} disabled={!currentSignature} data-testid="builder-apple-copy-btn">
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground text-center pt-1">
                      Gmail is the only provider that supports automatic signature updates. For others, click "Copy" and paste into your email settings.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div ref={previewContainerRef} className="relative">
              <div ref={previewRef} className="space-y-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Email Preview
                    </h3>
                    {currentSignature && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant={copySuccess ? "default" : "outline"}
                          size="sm"
                          onClick={() => { handleCopySignature(); setCopyBlink(false); }}
                          className={`${copySuccess ? "bg-green-600 hover:bg-green-700" : ""} ${copyBlink ? "animate-pulse ring-2 ring-green-400 bg-green-100 text-green-700 border-green-400" : ""}`}
                          data-testid="copy-btn"
                        >
                            {copySuccess ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Copy Signature</span>
                                <span className="sm:hidden">Copy</span>
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopyHtmlCode}
                            className="text-xs text-muted-foreground"
                            data-testid="copy-html-btn"
                          >
                            <Code className="w-3 h-3 mr-1" />
                            HTML
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownload}
                            data-testid="download-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Download</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSendPreviewEmail}
                            disabled={sendingPreview}
                            data-testid="send-preview-email-btn"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">{sendingPreview ? 'Sending...' : 'Preview as Email'}</span>
                            <span className="sm:hidden">{sendingPreview ? '...' : 'Email'}</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingSig(currentSignature);
                              setEditForm({
                                custom_message: currentSignature.custom_message || '',
                                disclaimer: currentSignature.disclaimer || '',
                                tagline: currentSignature.tagline || '',
                                font_family: currentSignature.font_family || '',
                                text_color: currentSignature.text_color || ''
                              });
                            }}
                            data-testid="edit-current-btn"
                          >
                            <PenLine className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Toggle between normal and ad-filled preview */}
                    {currentSignature && (
                      <div className="flex gap-2 mb-4">
                        <Button
                          variant={!showAdPreview ? "default" : "outline"}
                          size="sm"
                          onClick={() => setShowAdPreview(false)}
                          data-testid="normal-preview-btn"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Normal
                        </Button>
                        <Button
                          variant={showAdPreview ? "default" : "outline"}
                          size="sm"
                          onClick={handlePreviewWithAds}
                          data-testid="preview-with-ads-btn"
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Preview with Ads
                        </Button>
                      </div>
                    )}
                    
                    {showAdPreview && previewAds.length > 0 ? (
                      <>
                        <EmailPreview 
                          signatureHtml={adPreviewHtml || currentSignature?.html_content} 
                          userName={user?.full_name}
                        />
                        <div className="mt-6 pt-6 border-t border-dashed border-border">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                            Trusted Network — Slideshow Preview
                          </p>
                          <div className="px-4">
                            <AdSlideshow ads={previewAds} colorScheme={formData.color_scheme} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {livePreviewLoading && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            Updating preview…
                          </div>
                        )}
                        <EmailPreview 
                          signatureHtml={livePreviewHtml || currentSignature?.html_content} 
                          userName={user?.full_name}
                        />
                        
                        {/* Sample Ads Slideshow Preview */}
                        <div className="mt-6 pt-6 border-t border-dashed border-border">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                            How Ads Will Appear (My Trusted Network)
                          </p>
                          <div className="px-4">
                            <AdSlideshow ads={SAMPLE_ADS.map(ad => ({
                              ...ad,
                              qr_url: `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=tel:${ad.phone?.replace(/\D/g, '') || ''}`
                            }))} colorScheme={formData.color_scheme} />
                          </div>
                        </div>
                      </>
                    )}
              </div>
              </div>
            </div>
          </TabsContent>

          {/* Custom Signature Tab */}
          <TabsContent value="custom" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Type className="w-5 h-5" />
                        Create Your Own Signature
                      </CardTitle>
                      <div className="flex gap-1 bg-muted rounded-lg p-1">
                        <button 
                          onClick={() => setCustomMode('visual')}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${customMode === 'visual' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
                          data-testid="custom-visual-mode"
                        >
                          Visual
                        </button>
                        <button 
                          onClick={() => { setCustomMode('html'); setCustomHtml(blocksToHtml()); }}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${customMode === 'html' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`}
                          data-testid="custom-html-mode"
                        >
                          <Code className="w-3 h-3 inline mr-1" />
                          HTML
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {customMode === 'visual' ? (
                      <div className="space-y-3">
                        {/* Toolbar */}
                        <div className="flex gap-2 p-2 bg-muted/50 rounded-lg">
                          <Button size="sm" variant="outline" onClick={() => addBlock('text')} data-testid="add-text-block">
                            <Type className="w-3.5 h-3.5 mr-1" /> Text
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => addBlock('image')} data-testid="add-image-block">
                            <Image className="w-3.5 h-3.5 mr-1" /> Image
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => addBlock('divider')} data-testid="add-divider-block">
                            <span className="mr-1">—</span> Divider
                          </Button>
                        </div>

                        {/* Draggable blocks */}
                        <div className="space-y-2 min-h-[200px] border border-dashed border-border rounded-lg p-3">
                          {customBlocks.map((block, idx) => (
                            <div
                              key={block.id}
                              draggable
                              onDragStart={() => setDragItem(idx)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => { if (dragItem !== null) { moveBlock(dragItem, idx); setDragItem(null); } }}
                              className="group flex items-start gap-2 p-2 bg-white rounded-lg border border-border/50 hover:border-accent/50 transition-all cursor-move"
                              data-testid={`block-${block.id}`}
                            >
                              <GripVertical className="w-4 h-4 text-muted-foreground mt-1 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                {block.type === 'text' && (
                                  <div className="space-y-1">
                                    <input
                                      value={block.content}
                                      onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                                      className="w-full bg-transparent border-none outline-none text-sm"
                                      placeholder="Enter text..."
                                    />
                                    {editingBlock === block.id && (
                                      <input
                                        value={block.style}
                                        onChange={(e) => updateBlock(block.id, { style: e.target.value })}
                                        className="w-full text-xs bg-muted/50 rounded px-2 py-1 font-mono"
                                        placeholder="CSS: font-size:14px;color:#333;"
                                      />
                                    )}
                                  </div>
                                )}
                                {block.type === 'image' && (
                                  <div className="space-y-1">
                                    {block.src ? (
                                      <img src={resolveImageUrl(block.src)} alt={block.alt} className="max-w-[120px] rounded" />
                                    ) : (
                                      <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                                        <Upload className="w-4 h-4" /> Upload Image
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadBlockImage(block.id, e)} />
                                      </label>
                                    )}
                                    {editingBlock === block.id && (
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-1">
                                          <Link className="w-3 h-3 text-muted-foreground" />
                                          <input
                                            value={block.link || ''}
                                            onChange={(e) => updateBlock(block.id, { link: e.target.value })}
                                            className="w-full text-xs bg-muted/50 rounded px-2 py-1"
                                            placeholder="https://link-when-clicked.com"
                                          />
                                        </div>
                                        <input
                                          value={block.style}
                                          onChange={(e) => updateBlock(block.id, { style: e.target.value })}
                                          className="w-full text-xs bg-muted/50 rounded px-2 py-1 font-mono"
                                          placeholder="CSS styles..."
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                                {block.type === 'divider' && (
                                  <div style={{borderTop: '2px solid #3b82f6', width: '100%', margin: '4px 0'}} />
                                )}
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setEditingBlock(editingBlock === block.id ? null : block.id)} className="p-1 hover:bg-muted rounded">
                                  <Palette className="w-3.5 h-3.5 text-muted-foreground" />
                                </button>
                                <button onClick={() => removeBlock(block.id)} className="p-1 hover:bg-red-50 rounded">
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {customBlocks.length === 0 && (
                            <p className="text-center text-sm text-muted-foreground py-8">
                              Add text, images, or dividers using the toolbar above
                            </p>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground">
                          Drag blocks to reorder. Click the palette icon to edit styles. Ad slots are automatically added.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <textarea
                          value={customHtml}
                          onChange={(e) => setCustomHtml(e.target.value)}
                          className="w-full h-64 font-mono text-xs bg-[#1e1e1e] text-[#d4d4d4] rounded-lg p-4 border border-border/50 resize-y"
                          placeholder="Paste your HTML signature here..."
                          data-testid="custom-html-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Paste any HTML email signature. Ad slots will be automatically appended.
                        </p>
                      </div>
                    )}

                    <Button 
                      onClick={saveCustomSignature}
                      disabled={savingCustom}
                      className="w-full mt-4 bg-gradient-to-r from-[#1a1a2e] to-[#16213e] hover:opacity-90 text-white"
                      data-testid="save-custom-btn"
                    >
                      {savingCustom ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Save Custom Signature
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Live Preview */}
              <div className="space-y-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Live Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="bg-white rounded-lg border border-border/50 p-6 min-h-[200px]"
                      dangerouslySetInnerHTML={{ __html: customMode === 'html' ? customHtml : blocksToHtml() }}
                      data-testid="custom-preview"
                    />
                    <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Ad slots will be appended automatically when saved
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="library" className="mt-6">
            {signatures.length === 0 ? (
              <Card className="border-border/50 border-dashed">
                <CardContent className="p-8 text-center">
                  <div className="w-12 h-12 bg-muted rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <LayoutIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-medium mb-2">No signatures yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Generate your first signature using the builder
                  </p>
                  <Button onClick={() => setActiveTab('builder')}>
                    Go to Builder
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Marketplace Info Banner */}
                <Card className="border-accent/30 bg-accent/5">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Store className="w-5 h-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">Marketplace Signature</h4>
                      <p className="text-xs text-muted-foreground">
                        {marketplaceSignatureId 
                          ? "You have a signature set for the marketplace. Other professionals can see it and place ads." 
                          : "Select one signature to display in the marketplace so other pros can place ads on it."}
                      </p>
                    </div>
                    {marketplaceSignatureId && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleRemoveMarketplaceSignature}
                        data-testid="remove-marketplace-signature-btn"
                      >
                        Remove from Marketplace
                      </Button>
                    )}
                  </CardContent>
                </Card>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {signatures.map((sig) => {
                    const isMarketplaceSig = marketplaceSignatureId === sig.id;
                    return (
                      <Card 
                        key={sig.id} 
                        className={`border-border/50 hover:shadow-lg transition-all cursor-pointer group ${isMarketplaceSig ? 'ring-2 ring-accent' : ''} ${chooseBlinkActive && !isMarketplaceSig ? 'ring-2 ring-green-400 animate-pulse' : ''}`}
                        onClick={() => selectExistingSignature(sig)}
                        data-testid={`library-signature-${sig.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-lg"
                                style={{ 
                                  background: `linear-gradient(135deg, ${COLOR_SCHEMES.find(c => c.id === sig.color_scheme)?.color || '#1e3a5f'} 0%, ${COLOR_SCHEMES.find(c => c.id === sig.color_scheme)?.accent || '#3b82f6'} 100%)`
                                }}
                              />
                              <span className="text-sm font-medium capitalize">{sig.template_style}</span>
                              {isMarketplaceSig && (
                                <span className="flex items-center gap-1 text-[10px] bg-accent text-white px-1.5 py-0.5 rounded">
                                  <Store className="w-3 h-3" /> Marketplace
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {new Date(sig.created_at).toLocaleDateString()}
                              </span>
                              <button
                                onClick={(e) => handleDeleteSignature(e, sig.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500"
                                title="Delete signature"
                                data-testid={`delete-signature-${sig.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="h-20 overflow-hidden rounded border border-border bg-white p-2">
                            <div
                              dangerouslySetInnerHTML={{ __html: sig.html_content }}
                              className="transform scale-[0.35] origin-top-left"
                              style={{ width: '285%' }}
                            />
                          </div>
                          {/* Set as Marketplace button */}
                          <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSig(sig);
                                setEditForm({
                                  custom_message: sig.custom_message || '',
                                  disclaimer: sig.disclaimer || '',
                                  tagline: sig.tagline || '',
                                  font_family: sig.font_family || '',
                                  text_color: sig.text_color || ''
                                });
                              }}
                              data-testid={`edit-signature-${sig.id}`}
                            >
                              <PenLine className="w-3 h-3 mr-1" />
                              Edit Signature
                            </Button>
                            {isMarketplaceSig ? (
                              <div className="flex items-center gap-2 text-xs text-accent">
                                <CheckCircle className="w-4 h-4" />
                                <span className="font-medium">Active in Marketplace</span>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs h-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetMarketplaceSignature(sig.id);
                                }}
                                data-testid={`set-marketplace-${sig.id}`}
                              >
                                <Store className="w-3 h-3 mr-1" />
                                Set for Marketplace
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Marketplace Tab */}
          <TabsContent value="marketplace" className="mt-6">
            <AdMarketplace embedded onNavigateToApprovals={() => setActiveTab('approvals')} />
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="mt-6">
            <ApprovalCenter embedded />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <AnalyticsDashboard embedded />
          </TabsContent>

          {/* QR Code Tab = Digital Business Card with QR */}
          <TabsContent value="qrcode" className="mt-6">
            <QRCardTab user={user} signatures={signatures} />
          </TabsContent>


          {/* Settings Tab — Profile Info + Signature Customization */}
          <TabsContent value="settings" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                {/* Profile Information */}
                <Card className="border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Profile Information
                    </CardTitle>
                    <CardDescription className="text-xs">Edit your info here — changes reflect in your signature</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Profile Photo & Logo */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs mb-1.5 block">Profile Photo</Label>
                        <div className="relative group">
                          <div className="w-20 h-20 rounded-full border-2 border-dashed border-border overflow-hidden bg-muted flex items-center justify-center">
                            {profileData.profile_photo_url ? (
                              <img src={resolveImageUrl(profileData.profile_photo_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Camera className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>
                          <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            {uploadingProfile.photo ? <RefreshCw className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleProfileImageUpload(e, 'profile_photo_url')} data-testid="settings-profile-photo-upload" />
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1.5 block">Company Logo</Label>
                        <div className="relative group">
                          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-border overflow-hidden bg-muted flex items-center justify-center">
                            {profileData.logo_url ? (
                              <img src={resolveImageUrl(profileData.logo_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <ImageIcon className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>
                          <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                            {uploadingProfile.logo ? <RefreshCw className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleProfileImageUpload(e, 'logo_url')} data-testid="settings-logo-upload" />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Full Name</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.full_name || ''} onChange={e => setProfileData(p => ({...p, full_name: e.target.value}))} data-testid="settings-full-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Company</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.company || ''} onChange={e => setProfileData(p => ({...p, company: e.target.value}))} data-testid="settings-company" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Job Title</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.custom_position || ''} onChange={e => setProfileData(p => ({...p, custom_position: e.target.value}))} placeholder="e.g., CEO, Sales Manager" data-testid="settings-position" />
                      </div>
                      <div>
                        <Label className="text-xs">Profession</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.custom_profession || ''} onChange={e => setProfileData(p => ({...p, custom_profession: e.target.value}))} placeholder="e.g., Real Estate Agent" data-testid="settings-profession" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Phone</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.phone || ''} onChange={e => setProfileData(p => ({...p, phone: e.target.value}))} data-testid="settings-phone" />
                      </div>
                      <div>
                        <Label className="text-xs">Website</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.website || ''} onChange={e => setProfileData(p => ({...p, website: e.target.value}))} placeholder="https://" data-testid="settings-website" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.city || ''} onChange={e => setProfileData(p => ({...p, city: e.target.value}))} data-testid="settings-city" />
                      </div>
                      <div>
                        <Label className="text-xs">State</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.state || ''} onChange={e => setProfileData(p => ({...p, state: e.target.value}))} data-testid="settings-state" />
                      </div>
                      <div>
                        <Label className="text-xs">Zip</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.zip_code || ''} onChange={e => setProfileData(p => ({...p, zip_code: e.target.value}))} data-testid="settings-zip" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Street Address</Label>
                      <Input className="h-8 text-sm mt-1" value={profileData.street_address || ''} onChange={e => setProfileData(p => ({...p, street_address: e.target.value}))} data-testid="settings-address" />
                    </div>
                    {/* Social Links */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">LinkedIn URL</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.linkedin_url || ''} onChange={e => setProfileData(p => ({...p, linkedin_url: e.target.value}))} placeholder="https://linkedin.com/in/..." data-testid="settings-linkedin" />
                      </div>
                      <div>
                        <Label className="text-xs">Instagram URL</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.instagram_url || ''} onChange={e => setProfileData(p => ({...p, instagram_url: e.target.value}))} placeholder="https://instagram.com/..." data-testid="settings-instagram" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Twitter / X URL</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.twitter_url || ''} onChange={e => setProfileData(p => ({...p, twitter_url: e.target.value}))} data-testid="settings-twitter" />
                      </div>
                      <div>
                        <Label className="text-xs">Facebook URL</Label>
                        <Input className="h-8 text-sm mt-1" value={profileData.facebook_url || ''} onChange={e => setProfileData(p => ({...p, facebook_url: e.target.value}))} data-testid="settings-facebook" />
                      </div>
                    </div>
                    <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full h-9 text-sm" data-testid="settings-save-profile-btn">
                      {savingProfile ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Profile Info</>}
                    </Button>
                  </CardContent>
                </Card>

                {/* Gmail Auto-Sync */}
                <Card className="border-border/50" data-testid="gmail-sync-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4 text-red-500" />
                      Gmail Auto-Sync
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Connect Gmail to automatically update your signature when ads change
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {gmailConnected ? (
                      <>
                        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-green-800 dark:text-green-300" data-testid="gmail-connected-status">Gmail Connected</p>
                            {gmailConnectedAt && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                Since {new Date(gmailConnectedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Your Gmail signature updates automatically when ads are approved, revoked, or changed. You can also push manually below.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={handlePushToGmail}
                            disabled={pushingGmail || !signatures.length}
                            className="flex-1 h-9 text-sm"
                            data-testid="push-to-gmail-btn"
                          >
                            {pushingGmail ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Pushing...</> : <><Upload className="w-4 h-4 mr-2" />Push to Gmail Now</>}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleGmailDisconnect}
                            className="h-9 text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="gmail-disconnect-btn"
                          >
                            Disconnect
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-300">
                            Connect your Gmail account so your email signature automatically updates whenever ad placements change — no manual copy-paste needed.
                          </p>
                        </div>
                        <Button
                          onClick={handleGmailConnect}
                          disabled={gmailLoading}
                          className="w-full h-10 text-sm bg-red-600 hover:bg-red-700"
                          data-testid="gmail-connect-btn"
                        >
                          {gmailLoading ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                          ) : (
                            <><Chrome className="w-4 h-4 mr-2" />Connect Gmail for Auto-Updates</>
                          )}
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          Requires Gmail signature settings permission. You can disconnect anytime.
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sticky Preview for Settings tab */}
              <div ref={activeTab === 'settings' ? previewContainerRef : null} className="relative">
              <div ref={activeTab === 'settings' ? previewRef : null} className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Live Preview
                  </h3>
                  {livePreviewLoading && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </div>
                  )}
                </div>
                <EmailPreview 
                  signatureHtml={livePreviewHtml || currentSignature?.html_content} 
                  userName={user?.full_name}
                />
              </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      <ImageCropper
        open={cropState.open}
        onClose={() => setCropState({ open: false, src: null })}
        imageSrc={cropState.src}
        onCropComplete={handlePhotoCropDone}
        aspectRatio={1}
        shape="round"
      />

      {/* Edit Signature Modal */}
      <Dialog open={!!editingSig} onOpenChange={(open) => { if (!open) setEditingSig(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-signature-modal">
          <DialogHeader>
            <DialogTitle>Edit Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Professional Tagline</Label>
              <Input
                placeholder="Your trusted partner..."
                value={editForm.tagline}
                onChange={(e) => setEditForm(prev => ({ ...prev, tagline: e.target.value }))}
                data-testid="edit-tagline-input"
              />
            </div>
            
            {/* Font Family Selection */}
            <div className="space-y-2">
              <Label>Font Family (Google Fonts)</Label>
              <div className="grid grid-cols-3 gap-2">
                {FONT_FAMILIES.map((font) => (
                  <button
                    key={font.id}
                    onClick={() => setEditForm(prev => ({ ...prev, font_family: font.value }))}
                    className={`p-2 rounded-lg border text-left transition-all text-sm ${
                      editForm.font_family === font.value
                        ? 'border-accent ring-2 ring-accent/20 bg-accent/5'
                        : 'border-border hover:border-accent/50'
                    }`}
                    style={{ fontFamily: font.value || 'inherit' }}
                    data-testid={`edit-font-${font.id}`}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Text Color Selection */}
            <div className="space-y-2">
              <Label>Text Color</Label>
              <div className="flex flex-wrap gap-2">
                {TEXT_COLORS.map((color) => (
                  <button
                    key={color.id}
                    onClick={() => setEditForm(prev => ({ ...prev, text_color: color.value }))}
                    className={`group relative w-8 h-8 rounded-lg border-2 transition-all ${
                      editForm.text_color === color.value
                        ? 'border-foreground scale-110 shadow-lg'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value || '#f3f4f6' }}
                    title={color.name}
                    data-testid={`edit-text-color-${color.id}`}
                  >
                    {editForm.text_color === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Check className={`w-3 h-3 drop-shadow-lg`} style={{color: color.value ? '#fff' : '#333'}} />
                      </div>
                    )}
                    {color.id === 'default' && <span className="absolute inset-0 flex items-center justify-center text-[8px] text-gray-500">Auto</span>}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Custom Message / Note</Label>
              <textarea
                placeholder="Add a promotional message or note above your signature..."
                value={editForm.custom_message}
                onChange={(e) => setEditForm(prev => ({ ...prev, custom_message: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px] resize-y"
                data-testid="edit-custom-message"
              />
            </div>
            <div className="space-y-2">
              <Label>Legal Disclaimer</Label>
              <textarea
                placeholder="CONFIDENTIALITY NOTICE: This email and any attachments..."
                value={editForm.disclaimer}
                onChange={(e) => setEditForm(prev => ({ ...prev, disclaimer: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px] resize-y"
                data-testid="edit-disclaimer"
              />
            </div>
            {editingSig && (
              <div className="rounded-md border p-3 max-h-48 overflow-y-auto bg-white">
                <div dangerouslySetInnerHTML={{ __html: editingSig.html_content }} />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditingSig(null)} data-testid="edit-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                disabled={savingEdit}
                data-testid="edit-save-btn"
                onClick={async () => {
                  if (!editingSig) return;
                  setSavingEdit(true);
                  try {
                    const updated = await api.updateSignature(editingSig.id, editForm);
                    setSignatures(prev => prev.map(s => s.id === editingSig.id ? { ...s, ...updated } : s));
                    setCurrentSignature(prev => prev && prev.id === editingSig.id ? { ...prev, ...updated } : prev);
                    toast.success('Signature updated!');
                    setEditingSig(null);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Failed to save');
                  } finally {
                    setSavingEdit(false);
                  }
                }}
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <InstallSignatureWizard
        open={installWizardOpen}
        onClose={() => setInstallWizardOpen(false)}
        signatureId={installWizardSigId || signatures[0]?.id}
      />
    </Layout>
  );
};

export default SignatureBuilder;
